import { startTransaction } from '@sentry/browser';
import { StudyReelHostNames } from '@shared/extension/enums/study-reel-host-names.enum';
import { safeSentryCaptureException } from '@shared/sentry';

const HOST_NOT_FOUND_ERROR = 'Specified native messaging host not found.';

export async function sendStudyReelMessage(host: StudyReelHostNames, message: Record<string, unknown>): Promise<boolean> {
  return await new Promise((resolve, reject) => {
    const port = chrome.runtime.connectNative(host);

    const messageListener = function (): void {
      // Clean up the listeners
      port.onMessage.removeListener(messageListener);
      port.onDisconnect.removeListener(disconnectListener);
      port.disconnect();
      resolve(true); // Resolve the promise with true when the message is received
    };

    const disconnectListener = function (): void {
      // Clean up the listeners
      port.onMessage.removeListener(messageListener);
      port.onDisconnect.removeListener(disconnectListener);

      if (chrome.runtime.lastError) {
        reject(chrome.runtime.lastError);
      }

      resolve(false); // Resolve the promise with false when disconnected
    };

    port.onMessage.addListener(messageListener);
    port.onDisconnect.addListener(disconnectListener);

    port.postMessage(message);
  });
}

export async function safeSendStudyReelMessage(
  host: StudyReelHostNames,
  message: Record<string, unknown>
): Promise<boolean> {
  const transaction = startTransaction({ name: 'sendStudyReelMessage' });
  const span = transaction.startChild({
    op: 'sendStudyReelMessage',
  });

  try {
    const response = await sendStudyReelMessage(host, message);
    span.setStatus(response ? 'ok' : 'unknown_error');
    return response;
  } catch (error) {
    span.setStatus('unknown_error');
    if (error?.message !== HOST_NOT_FOUND_ERROR) {
      safeSentryCaptureException(error);
    }
    return false;
  } finally {
    span.finish();
    transaction.finish();
  }
}
